// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '../../../../services/notification-service';
import { UserService } from '../../../../services/user-service';
import { ISaveKeyDialogConfig, SaveKeyDialogComponent } from '../save-key-dialog/save-key-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';
import { SecurityApproverState } from '../../../../types/security-requests';
import { finalize } from 'rxjs/operators';
import {UserRole} from '../../../../types/user';

@Component({
    selector: 'app-edit-peer-user-dialog',
    templateUrl: './edit-peer-user-dialog.component.html',
    styleUrls: ['./edit-peer-user-dialog.component.scss'],
    standalone: false
})
export class EditPeerUserDialogComponent implements OnInit {
  registerForm: UntypedFormGroup;
  lastLogin: string;
  checkingEmailExists = false;
  selectedLanguage: string;
  approverPending = false;
  approverInitialValue = false;

  fTitle: string;
  fFName: string;
  fLName: string;
  fEmail: string;
  fEmailExists: string;
  fEmailInvalid: string;
  fJob: string;
  fDepartment: string;
  fLocation: string;
  fRole: string;
  fAdmin: string;
  fUser: string;
  fGenerateKeys: string;
  fNewPass: string;
  fInvite: string;
  fCancel: string;
  fSave: string;
  constructor(
    public dialogRef: MatDialogRef<EditPeerUserDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private notificationService: NotificationService,
    public dialog: MatDialog,
    private userService: UserService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.translate.get(['EPU_TITLE', 'FIRST', 'LAST', 'EPU_EMAIL', 'EPU_EMAIL_EXISTS', 'CANCEL_CAP',
      'EPU_EMAIL_INVALID', 'EPU_JOB', 'EPU_DEPARTMENT', 'EPU_LOCATION', 'EPU_ROLE', 'EPU_ADMIN',
      'EPU_USER', 'EPU_GEN_KEYS', 'EPU_NEW_PASS', 'EPU_INVITE', 'EPU_CANCEL', 'SAVE_CAP']).subscribe(vals => {
      this.fTitle = vals['EPU_TITLE'];
      this.fFName = vals['FIRST'];
      this.fLName = vals['LAST'];
      this.fEmail = vals['EPU_EMAIL'];
      this.fEmailExists = vals['EPU_EMAIL_EXISTS'];
      this.fEmailInvalid = vals['EPU_EMAIL_INVALID'];
      this.fJob = vals['EPU_JOB'];
      this.fDepartment = vals['EPU_DEPARTMENT'];
      this.fLocation = vals['EPU_LOCATION'];
      this.fRole = vals['EPU_ROLE'];
      this.fAdmin = vals['EPU_ADMIN'];
      this.fUser = vals['EPU_USER'];
      this.fGenerateKeys = vals['EPU_GEN_KEYS'];
      this.fNewPass = vals['EPU_NEW_PASS'];
      this.fInvite = vals['EPU_INVITE'];
      this.fCancel = vals['CANCEL_CAP'];
      this.fSave = vals['SAVE_CAP'];
    });
    this.registerForm = this.formBuilder.group({
      fname: ['', [Validators.required, Validators.maxLength(255)]],
      lname: ['', [Validators.required, Validators.maxLength(255)]],
      email: [{'value': '', disabled: this.data.user?.source === 2}, [
        Validators.required,
        Validators.pattern(CustomValidators.emailPattern),
        CustomValidators.maxByteLength(255)
      ]],
      title: [''],
      department: [''],
      location: [''],
      generateKeys: [false],
      keyPassword: [''],
      role: [''],
      sendEmail: [false],
      security_approver: [false],
      notifyApprovers: [false],
    });
    this.registerForm.controls['notifyApprovers'].disable();
    this.registerForm.controls['fname'].setValue(this.data.user.fname);
    this.registerForm.controls['lname'].setValue(this.data.user.lname);
    this.registerForm.controls['email'].setValue(this.data.user.email);
    this.registerForm.controls['title'].setValue(this.data.user.title);
    this.registerForm.controls['department'].setValue(this.data.user.department);
    this.registerForm.controls['location'].setValue(this.data.user.location);
    this.registerForm.controls['role'].setValue(this.data.user.role);
    this.registerForm.controls['keyPassword'].disable();
    this.lastLogin = this.data.user.last_login;

    switch(this.data.user.security_approver) {
      case SecurityApproverState.APPROVER:
        this.registerForm.controls['security_approver'].setValue(true);
        this.approverPending = false;
        break;

      case SecurityApproverState.APPROVER_PENDING:
        this.registerForm.controls['security_approver'].setValue(false);
        this.registerForm.controls['security_approver'].disable();
        this.approverPending = true;
        break;

      case SecurityApproverState.NOT_APPROVER:
        this.registerForm.controls['security_approver'].setValue(false);
        this.approverPending = false;
        break;

      case SecurityApproverState.NOT_APPROVER_PENDING:
        this.registerForm.controls['security_approver'].setValue(true);
        this.registerForm.controls['security_approver'].disable();
        this.approverPending = true;
        break;
    }

    this.approverInitialValue = this.registerForm.controls['security_approver'].value;

    if (!this.data.user.active || this.data.disableApproverCheckbox) {
      this.registerForm.controls['security_approver'].disable();
    }

    // TODO: If we don't want the MSP to be able to change a security approver.
    // TODO: This is currently what the API is checking, waiting to ask Dave W
    // TODO: what the behavior should be.
    //
    // if (!this.data.sameOrg || !this.data.showSecurityApproverCheckbox) {
    //   this.registerForm.controls['security_approver'].disable();
    // }

    this.selectedLanguage = localStorage.getItem('language') || 'en';
  }

  get email() {
    return this.registerForm.controls['email'];
  }

  get doNotSendEmail() {
    return !this.registerForm.controls['sendEmail']?.value;
  }

  ngOnInit() {
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onSubmit(): void {
    const user = this.registerForm.value;
    user.admin = user.admin === 'admin';
    user.id = this.data.user.id;
    user.email_notify_language = this.selectedLanguage;
    if (user.generateKeys && user.keyPassword) {
      const dialogConfig: ISaveKeyDialogConfig = {password: user.keyPassword};
      const saveDialogRef = this.dialog.open(SaveKeyDialogComponent, {
        width: '600px',
        height: 'auto',
        disableClose: true,
        data: dialogConfig,
      });
      saveDialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.dialogRef.close(user);
        }
      });
    } else {
      this.dialogRef.close(user);
    }
  }

  isEmailFieldInvalid(field: string) {
    return (
      (!this.registerForm.get(field).valid && this.registerForm.get(field).touched) ||
      (this.registerForm.get(field).untouched)
    );
  }

  onEmailBlur() {
    if (this.isEmailFieldInvalid('email')) {
      // Email is invalid, don't check email yet!
    } else {
      if (this.registerForm.controls.email.errors) {
        return;
      }
      this.checkingEmailExists = true;
      this.userService.checkUserEmailExistsForUser(this.data.user.id, this.registerForm.value.email)
          .pipe(finalize(() => this.checkingEmailExists = false))
          .subscribe(
              next => {
                // This is a case where we only consider if the email exists regardless of verified or invited.
                if (next.exists) {
                  this.email.setErrors({'exists': true});
                }
              },
              () => {
                // Error state, probably email pattern
                this.email.setErrors({'pattern': true});
              }
          );
    }
  }

  changePasswordEnabled(event) {
    const control = this.registerForm.get('keyPassword');
    if (event.checked) {
      control.enable();
    } else {
      control.disable();
    }
  }

  onLanguageChanged(language) {
    this.selectedLanguage = language;
  }

  approverChanged() {
    const notifyApprovers = this.registerForm.controls['notifyApprovers'];
    const securityApprover = this.registerForm.controls['security_approver'];
    if (securityApprover.value != this.approverInitialValue) {
      notifyApprovers.enable();
      notifyApprovers.setValue(true);
    } else {
      notifyApprovers.disable();
      notifyApprovers.setValue(false);
    }
  }

  protected readonly UserRole = UserRole;
}
